import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useThemeTemplateMutations } from '~/hooks/use-theme-templates';
import { themeTemplateSchema, EMPTY_THEME_TEMPLATE, type ThemeTemplateFormValues } from '~/lib/schemas/theme-template';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: ThemeTemplateFormValues | null;
}

/** Add / edit a theme template (Πρότυπο Θέματος). */
export function ThemeTemplateEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useThemeTemplateMutations();
	const form = useForm<ThemeTemplateFormValues>({
		resolver: zodResolver(themeTemplateSchema) as Resolver<ThemeTemplateFormValues>,
		defaultValues: initial ?? EMPTY_THEME_TEMPLATE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_THEME_TEMPLATE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.themeTemplates.editTitle') : t('catalog.themeTemplates.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.themeTemplates.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.themeTemplates.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={200} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('catalog.themeTemplates.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={100} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="tt-isfp">{t('catalog.themeTemplates.columns.isForProducts')}</Label>
							<Switch id="tt-isfp" checked={watch('isfp')} onCheckedChange={next => setValue('isfp', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="tt-isfc">{t('catalog.themeTemplates.columns.isForCategories')}</Label>
							<Switch id="tt-isfc" checked={watch('isfc')} onCheckedChange={next => setValue('isfc', next, { shouldDirty: true })} />
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
